import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Model from "flarum/common/Model";

/**
 * A single reaction bucket, as sent by MessageResource::reactionSummary().
 * Pre-aggregated on the server so the client never holds every reaction row.
 */

export default class Message extends Model {
  constructor() {
    super(...arguments);
    // ── Content ────────────────────────────────────────────────────────────────
    /**
     * Author-facing source text. Null on a deleted message the actor may not see.
     */
    _defineProperty(this, "content", Model.attribute("content"));
    _defineProperty(this, "contentHtml", Model.attribute("contentHtml"));
    _defineProperty(this, "type", Model.attribute("type"));
    _defineProperty(this, "systemKey", Model.attribute("systemKey"));
    _defineProperty(this, "systemData", Model.attribute("systemData"));
    // ── Position ───────────────────────────────────────────────────────────────
    _defineProperty(this, "number", Model.attribute("number"));
    _defineProperty(this, "channelId", Model.attribute("channelId"));
    _defineProperty(this, "threadId", Model.attribute("threadId"));
    _defineProperty(this, "replyToId", Model.attribute("replyToId"));
    _defineProperty(this, "createdAt", Model.attribute("createdAt", Model.transformDate));
    _defineProperty(this, "editedAt", Model.attribute("editedAt", Model.transformDate));
    _defineProperty(this, "deletedAt", Model.attribute("deletedAt", Model.transformDate));
    /**
     * Advanced by every change the row can undergo — an edit, a deletion, a pin,
     * a reaction. The polling fallback reconciles against it; see pollChanges()
     * in forum/index.tsx.
     */
    _defineProperty(this, "updatedAt", Model.attribute("updatedAt", Model.transformDate));
    _defineProperty(this, "isDeleted", Model.attribute("isDeleted"));
    /**
     * Whether someone other than the author removed it.
     *
     * Distinct from `isRedacted()`, which only says the text was withheld from
     * this reader — true for a self-deleted message too.
     */
    _defineProperty(this, "isModeratorDeleted", Model.attribute("isModeratorDeleted"));
    _defineProperty(this, "isEdited", Model.attribute("isEdited"));
    _defineProperty(this, "isPinned", Model.attribute("isPinned"));
    _defineProperty(this, "pinnedAt", Model.attribute("pinnedAt", Model.transformDate));
    // ── Engagement ─────────────────────────────────────────────────────────────
    _defineProperty(this, "reactionSummary", Model.attribute("reactionSummary"));
    _defineProperty(this, "mentionedUsers", Model.attribute("mentionedUsers"));
    _defineProperty(this, "mentionsChannelWide", Model.attribute("mentionsChannelWide"));
    _defineProperty(this, "isBookmarked", Model.attribute("isBookmarked"));
    /** Whether *this* reader has an open report against it. */
    _defineProperty(this, "isFlagged", Model.attribute("isFlagged"));
    /**
     * Open reports on this message.
     *
     * Withheld from anyone without `ramon-chat.moderate`, so it is undefined rather
     * than zero for ordinary readers — a visible count would tell everyone which
     * messages are being reported, and tell an author they had been.
     */
    _defineProperty(this, "flagsCount", Model.attribute("flagsCount"));
    // ── Capability flags ───────────────────────────────────────────────────────
    _defineProperty(this, "canEdit", Model.attribute("canEdit"));
    _defineProperty(this, "canDelete", Model.attribute("canDelete"));
    _defineProperty(this, "canReact", Model.attribute("canReact"));
    _defineProperty(this, "canReply", Model.attribute("canReply"));
    _defineProperty(this, "canCreateThread", Model.attribute("canCreateThread"));
    _defineProperty(this, "canMove", Model.attribute("canMove"));
    _defineProperty(this, "canPin", Model.attribute("canPin"));
    _defineProperty(this, "canFlag", Model.attribute("canFlag"));
    /** Whether the row itself may be removed, tombstone and all. */
    _defineProperty(this, "canForceDelete", Model.attribute("canForceDelete"));
    // ── Relationships ──────────────────────────────────────────────────────────
    _defineProperty(this, "user", Model.hasOne("user"));
    _defineProperty(this, "editedBy", Model.hasOne("editedBy"));
    _defineProperty(this, "deletedBy", Model.hasOne("deletedBy"));
    _defineProperty(this, "replyTo", Model.hasOne("replyTo"));
    _defineProperty(this, "thread", Model.hasOne("thread"));
    _defineProperty(this, "channel", Model.hasOne("channel"));
    _defineProperty(this, "uploads", Model.hasMany("uploads"));
  }
  // ── Derived helpers ────────────────────────────────────────────────────────

  isSystem() {
    return this.type() === "system";
  }

  /**
   * Posted by the chat's bot rather than a person.
   *
   * Not a system message: it has real content and renders through the ordinary
   * message path. Only the author differs — there is no user record, so the name
   * and avatar come from the admin's settings.
   */
  isBot() {
    return this.type() === "bot";
  }

  /**
   * True when the row exists but its text was withheld — a moderator-removed
   * message shown to someone who may not read it. The stream renders a
   * tombstone so it does not silently reflow.
   */
  isRedacted() {
    return Boolean(this.isDeleted()) && this.content() === null;
  }

  /**
   * Whether this message mentions the current user, either by name or through
   * @here / @all. Drives the mention highlight.
   */
  mentionsActor() {
    var _this$mentionedUsers;
    const actor = app.session.user;
    if (!actor) return false;
    if (this.mentionsChannelWide()) return true;
    return ((_this$mentionedUsers = this.mentionedUsers()) != null ? _this$mentionedUsers : []).includes(Number(actor.id()));
  }

  /**
   * Whether this message should render collapsed under the previous one: same
   * author, close in time, and neither is a system message.
   */
  isGroupedWith(previous, withinSeconds) {
    if (withinSeconds === void 0) {
      withinSeconds = 300;
    }
    if (!previous) return false;
    if (this.isSystem() || previous.isSystem()) return false;
    if (this.threadId() !== previous.threadId()) return false;

    // Two bot posts in a row have the same author, the same as two from one
    // person. Without this they compare `user()` — null for both — and fail the
    // "unknown author" check below, so consecutive announcements each drew their
    // own avatar and header instead of collapsing into a run. A bot post and a
    // human one are never the same author, whichever way round they fall.
    if (this.isBot() || previous.isBot()) {
      if (!this.isBot() || !previous.isBot()) return false;
    } else {
      const a = this.user();
      const b = previous.user();
      if (!a || !b || a.id() !== b.id()) return false;
    }

    // Reached by both branches: same author is necessary but not sufficient, and
    // the time window applies to the bot exactly as it does to anyone else.
    const t1 = this.createdAt();
    const t2 = previous.createdAt();
    if (!t1 || !t2) return false;
    return (t1.getTime() - t2.getTime()) / 1000 <= withinSeconds;
  }
  totalReactions() {
    var _this$reactionSummary;
    const summary = (_this$reactionSummary = this.reactionSummary()) != null ? _this$reactionSummary : {};
    return Object.values(summary).reduce((sum, entry) => sum + entry.count, 0);
  }
  apiEndpoint() {
    return "/chat-messages" + (this.exists ? "/" + this.id() : "");
  }
}
flarum.reg.add('ramon-chat', 'common/models/Message', Message);